import React, { useState } from 'react';
import { PageDefinition } from '@fullstory/proto/fs/api/pages';
import { ValidationError } from '@fullstory/proto/fs/services/paige';
import { CLOSED, EMPTY_PAGE_DEF } from '../constants';
import { usePageDefDependencies } from './usePageDefDependencies';
import { usePageDefSignalStudio, PageDefSignalStudioProps } from './usePageDefSignalStudio';

type ModalInput = PageDefinition | typeof CLOSED;

export const usePageModalProps = () => {
  // State for the page def modal.
  // CLOSED    -> modal closed
  // undefined -> new page def
  // object    -> edit page def
  const [modalInput, setModalInput] = useState<ModalInput>(CLOSED);
  const [errors, setErrors] = useState<ValidationError[]>();

  // Trigger create modal
  const onCreate = () => setModalInput(EMPTY_PAGE_DEF);

  const onClose = () => setModalInput(CLOSED);

  return {
    modalInput,
    setModalInput,
    errors,
    setErrors,
    onCreate,
    onClose,
  };
};

type PageDefModalContainerProps = {
  modalInput: ModalInput;
  setModalInput: (modalInput: ModalInput) => void;
  errors: ValidationError[] | undefined;
  setErrors: (errors: ValidationError[] | undefined) => void;
  onClose: (saved?: boolean) => void;
  initialSpecSlugs?: string[];
  onSave?: ReturnType<typeof usePageDefDependencies>['onSave'];
  isSuggestion?: boolean;
} & Omit<ReturnType<typeof usePageModalProps>, 'onCreate'>;

const PageSignalStudio = (props: PageDefSignalStudioProps) => {
  const { SignalStudio, signalStudioProps } = usePageDefSignalStudio(props);

  if (!SignalStudio) return null;

  return <SignalStudio {...signalStudioProps} />;
};

export const PageDefModalComponent = (props: PageDefModalContainerProps) => {
  const { modalInput, setModalInput, setErrors } = props;

  const onClose = (saved?: boolean) => {
    props.onClose?.(saved);
    setErrors(undefined);
    setModalInput(CLOSED);
  };

  const pageDefDependencies = usePageDefDependencies(modalInput == CLOSED ? 0 : modalInput.id);

  const page = modalInput === CLOSED ? EMPTY_PAGE_DEF : modalInput;

  if (modalInput == CLOSED) return null;

  return (
    <PageSignalStudio
      page={page}
      setPage={setModalInput}
      onClose={onClose}
      initialErrors={[]}
      isSuggestion={props.isSuggestion}
      {...pageDefDependencies}
    />
  );
};
